/**
 * The loading state for a button, matching what Apple does.
 *
 * iOS keeps the button's fill, size and radius, replaces the label with a
 * spinner, and makes it non-interactive. It does not grey out and it does not
 * change its copy — a button that says "Saving…" in place of "Save" is telling
 * you something you already know, and greying it out reads as "unavailable"
 * rather than "working".
 *
 * Usage:
 *
 *   <button
 *     disabled={saving || !valid}
 *     aria-busy={saving}
 *     className="... disabled:opacity-50"
 *     style={busyStyle(saving)}
 *   >
 *     {saving ? <ButtonSpinner /> : 'Save changes'}
 *   </button>
 *
 * The spinner inherits `currentColor`, so a biscayne button gets a white
 * spinner and an outline button gets a biscayne one with no extra wiring.
 */
export default function ButtonSpinner({ size = 20 }: { size?: number }) {
  return (
    <span
      role="status"
      aria-label="Working"
      className="inline-block animate-spin"
      style={{
        width: size,
        height: size,
        // A ring with one transparent quadrant, drawn in the label's own
        // colour. Border rather than an SVG so it scales with `size` and needs
        // no fill/stroke plumbing.
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        borderRadius: '50%',
      }}
    />
  );
}
